/**
 * The school key: the name an app goes by in the generated build config
 * (`mobile-app-config.json` top-level key) and the token build / OTA
 * dispatches select it with.
 *
 * It is ALWAYS derived from `schoolAppId` — the `schoolAppId` without its
 * "NNNN." prefix, so `"0026.st-marks-school-of-texas"` → `"st-marks-school-of-texas"`.
 * `MobileApp.configKey` used to override it for eleven backfilled schools; that
 * override is retired (SCH-2084) and no reader consults it. Backend and web
 * share this one definition because a trigger that derived the key differently
 * from the config generator would ask EAS to build a school the config does
 * not contain.
 */
export function deriveSchoolKey(schoolAppId: string): string {
  const dot = schoolAppId.indexOf('.')
  return dot === -1 ? schoolAppId : schoolAppId.slice(dot + 1)
}

/** {@link deriveSchoolKey} for a row that may not have a `schoolAppId` yet. Null ⇒ no key. */
export function schoolKeyFor({ schoolAppId }: { schoolAppId?: string | null | undefined }): string | null {
  return schoolAppId ? deriveSchoolKey(schoolAppId) : null
}

/**
 * The `NNNN-` School-id prefix a new Expo project slug carries (`0037-hockaday-school`). Four or more
 * digits then a hyphen, so the `<slug>` half of a prefixed slug and of an unprefixed one read the same.
 */
export const EXPO_SLUG_SCHOOL_PREFIX = /^\d{4,}-/

/**
 * The School's numeric id zero-padded to 4 digits: the prefix the school app id, a new Expo slug and a
 * new Firebase project id (`sj-0073-reich-hebrew-academy`) all start with.
 */
export function paddedSchoolId(schoolId: bigint): string {
  return schoolId.toString().padStart(4, '0')
}

/**
 * An Expo slug without its `NNNN-` School-id prefix: `0037-hockaday-school` → `hockaday-school`. A slug
 * with no prefix (every project created before the prefix existed) comes back unchanged. It is also the
 * app's custom URL scheme, which must start with a letter.
 */
export function expoSlugBase(slug: string): string {
  return slug.replace(EXPO_SLUG_SCHOOL_PREFIX, '')
}

/**
 * The slug a NEW Expo project is created under: the School's numeric id zero-padded to 4 digits, a
 * hyphen, then the app slug — `0037-hockaday-school`, so the Expo dashboard lists the project by
 * school id the way OneSignal lists its app by `schoolAppId` (`0037.hockaday-school`).
 *
 * A hyphen, not the school app id's dot: Expo rejects a dot in a slug (eas-cli's `isValidSlugStrict`,
 * `/^[a-zA-Z0-9_\-]+$/`, "from xdl-schemas slug"). Idempotent — a slug already carrying a prefix
 * keeps one. Only the create paths call this; an existing project keeps the slug it has.
 */
export function formatExpoProjectSlug(schoolId: bigint, slug: string): string {
  return `${paddedSchoolId(schoolId)}-${expoSlugBase(slug)}`
}

/**
 * `MobileApp.schoolAppId` convention, confirmed against the live registry (2026-08-18): the
 * School's numeric id zero-padded to 4 digits, a dot, then the app's Expo slug without its
 * `NNNN-` prefix — e.g. `0018.idyllwild-arts-academy`, and `0037.hockaday-school` for the Expo
 * project `0037-hockaday-school`. Its school key (the part after the dot) is therefore the
 * unprefixed slug, which is what the per-school asset directory and `deriveSchoolKey` expect.
 *
 * The numeric id never leaves the backend, so only the backend calls this: for the registry create
 * and for provisioning write-back.
 */
export function formatSchoolAppId(schoolId: bigint, slug: string): string {
  return `${paddedSchoolId(schoolId)}.${expoSlugBase(slug)}`
}

/** Google's hard ceiling on a project ID. */
const PROJECT_ID_MAX_LENGTH = 30

/**
 * The slug half of a `schoolAppId` (`0073.reich-hebrew-academy` →
 * `reich-hebrew-academy`), lowercased.
 *
 * Lowercasing is not cosmetic: Google project IDs admit lowercase only, and the
 * fleet holds at least two apps whose `schoolAppId` is mixed case
 * (`0000.School-Journey`, `0026.St-Marks-School-of-Texas`). Without it the
 * allocated ID would simply be rejected by Resource Manager.
 */
export function slugFromSchoolAppId(schoolAppId: string | null): string | null {
  if (!schoolAppId) return null
  const slug = schoolAppId.includes('.') ? schoolAppId.slice(schoolAppId.indexOf('.') + 1) : schoolAppId
  const normalized = slug
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
  return normalized.length > 0 ? normalized : null
}

/** Cut to `limit` and drop a trailing hyphen, which Google rejects. */
function cut(value: string, limit: number): string {
  return value.slice(0, limit).replace(/-+$/, '')
}

/** `<head>-<slug>`, with only the slug cut so the whole fits `limit`. The head is never cut. */
export function joinWithinLimit(head: string, slug: string, limit: number): string {
  const tail = cut(slug, limit - head.length - 1)
  return tail ? `${head}-${tail}` : head
}

/**
 * Candidate `index` for one label: `sj-<padded>-<slug>`, mirroring the registry
 * key `<padded>.<slug>` so projects sort by school number. Only the slug is cut
 * to fit 30 characters, so `sj-<padded>` always survives; a trailing hyphen is
 * dropped. Index > 0 appends `-<index+1>` AFTER re-cutting the slug so the
 * suffix always survives the ceiling.
 */
export function projectIdCandidate(input: { paddedSchoolKey: string; slug: string }, index: number): string {
  const head = `sj-${input.paddedSchoolKey}`
  if (index === 0) return joinWithinLimit(head, input.slug, PROJECT_ID_MAX_LENGTH)
  const suffix = `-${index + 1}`
  return `${joinWithinLimit(head, input.slug, PROJECT_ID_MAX_LENGTH - suffix.length)}${suffix}`
}
